// Popup de una tienda en «Turbo · En vivo». Leaflet lo dibuja como HTML suelto
// y se rearma con cada sondeo (estados, choferes, distancias): lo que el
// coordinador eligió vive en el DOM y hay que cuidarlo.

import { driverName } from "./driverName";
import { TURBO_ESTADO_COLOR, TURBO_ESTADO_LABEL, TurboHoy } from "./mapLayers";

export type Store = { id: number; name: string; address: string | null; lat: number; lng: number; estado: "SIN_TURBO" | "CON_TURBO" | "ESCALADO"; turbos: { total: number; abiertos: number; escalados: number; ofreciendo: number; entregados: number } };
export type Driver = {
  driver_id: number; username: string; first_name: string | null; last_name: string | null; phone: string | null; vehiculo: string | null; tipo: "moto" | "auto";
  lat: number; lng: number; recorded_at: string; source: string; turbo_enabled: boolean; turbo_active: boolean; turbo_route_id: number | null; turbos_abiertos: number;
  estado: "ESPERANDO" | "A_RETIRAR" | "ENTREGANDO" | "APAGADO";
};
// Lo del último sondeo que usa el popup.
export type VivoPopup = { turbos: TurboHoy[]; drivers: Driver[] };

export const SIN_SENAL_MIN = 10;
export const COLOR = { gris: "#8b949e", verde: "#3fb950", rojo: "#f85149", azul: "#4f8cff", naranja: "#f0883e", violeta: "#a371f7", amarillo: "#d29922" };
export const DRIVER_ESTADO: Record<Driver["estado"], { label: string; color: string }> = {
  ESPERANDO: { label: "esperando un Turbo", color: COLOR.azul },
  A_RETIRAR: { label: "yendo a retirar", color: COLOR.naranja },
  ENTREGANDO: { label: "entregando", color: COLOR.verde },
  APAGADO: { label: "con el Turbo apagado", color: COLOR.gris },
};
export const STORE_ESTADO: Record<Store["estado"], { label: string; color: string }> = {
  SIN_TURBO: { label: "sin Turbo hoy", color: COLOR.gris },
  CON_TURBO: { label: "con Turbo en curso", color: COLOR.verde },
  ESCALADO: { label: "Turbo sin chofer: pasó a administración", color: COLOR.rojo },
};

// La hora en Argentina, no la de la PC (una PC en otra zona mostraba otra hora).
export const fmtTime = (v: string | null | undefined) => (v ? new Date(v).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit", timeZone: "America/Argentina/Buenos_Aires" }) : "—");
export const fmtDia = (v: string) => new Date(v).toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", timeZone: "America/Argentina/Buenos_Aires" });
// Una hora de un Turbo. Si se arrastra de un día anterior, con el día adelante
// («30/10 23:40»): sin eso, uno de ayer se leía como de hoy.
export const fmtHoraTurbo = (t: Pick<TurboHoy, "de_dias_anteriores">, v: string | null | undefined) => (v && t.de_dias_anteriores ? `${fmtDia(v)} ${fmtTime(v)}` : fmtTime(v));
export const minutosDesde = (iso: string, now: number) => Math.max(0, (now - new Date(iso).getTime()) / 60000);
export const nombre = (d: { username: string; first_name: string | null; last_name: string | null }) => driverName(d);
export const esc = (s: unknown) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c] as string));
export const distKm = (a: { lat: number; lng: number }, b: { lat: number; lng: number }) => {
  const R = 6371, dLat = ((b.lat - a.lat) * Math.PI) / 180, dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const x = Math.sin(dLat / 2) ** 2 + Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
};
export const despachoVivoDe = (t: TurboHoy) => !!t.despacho && t.despacho.status !== "CANCELLED" && ["PLANNED", "ASSIGNED"].includes(t.despacho.route_status ?? "");

// El contenido del popup de una tienda: sus Turbos abiertos y, para los que
// están SIN_CHOFER, la asignación a mano. Solo esos: un Turbo en camino sin
// recorrido o no entregado no tiene a quién asignar (fallaba siempre) y la
// asignación a mano da por visto «sin chofer» (ver pedidoAsignar).
export function htmlTienda(s: Store, live: VivoPopup, ahora: number): string {
  const turbos = live.turbos.filter((t) => t.store_id === s.id);
  const abiertos = turbos.filter((t) => !t.terminal);
  // Los cerrados, por separado: un cancelado no es un entregado.
  const entregados = turbos.filter((t) => t.estado === "ENTREGADO").length;
  const cancelados = turbos.filter((t) => t.estado === "CANCELADO").length;
  // Primero los que están esperando (libres y con el Turbo prendido), por
  // cercanía; después los demás, también por cercanía.
  const candidatos = live.drivers
    .map((d) => ({ d, km: distKm(d, s), libre: d.estado === "ESPERANDO" && minutosDesde(d.recorded_at, ahora) <= SIN_SENAL_MIN ? 0 : 1 }))
    .sort((a, b) => a.libre - b.libre || a.km - b.km);
  const opciones = candidatos.map(({ d, km }) => `<option value="${d.driver_id}" data-nombre="${esc(nombre(d))}">${esc(nombre(d))} · ${d.tipo} · ${km.toFixed(1)} km · ${esc(DRIVER_ESTADO[d.estado].label)}${minutosDesde(d.recorded_at, ahora) > SIN_SENAL_MIN ? " · sin señal" : ""}</option>`).join("");
  const filas = abiertos.map((t) => {
    const st = TURBO_ESTADO_LABEL[t.estado];
    const vence = t.vence_at ? ` · vence ${fmtHoraTurbo(t, t.vence_at)}` : "";
    const km = t.distancia_km != null ? `${Number(t.distancia_km).toFixed(1)} km` : "";
    // El motivo (en rojo) es para los que están SIN_CHOFER. Uno en camino o no
    // entregado sin chofer de Lynx (lo llevó la tienda por fuera) no tiene un
    // «motivo»: se dice qué pasa, sin alarma.
    const quien = t.chofer
      ? `<div class="m">${esc(nombre(t.chofer))}${t.detalle ? ` · ${esc(t.detalle)}` : ""}${km ? ` · ${km}` : ""}</div>`
      : t.estado === "SIN_CHOFER"
        ? `<div class="m" style="color:${TURBO_ESTADO_COLOR.SIN_CHOFER}">${motivoSinChofer(t)}</div>${km ? `<div class="m">comprador a ${km} de la tienda</div>` : ""}`
        : `<div class="m">${t.detalle ? `${esc(t.detalle)} · ` : ""}sin chofer de Lynx${km ? ` · comprador a ${km} de la tienda` : ""}</div>`;
    const acciones = t.estado !== "SIN_CHOFER" ? "" : `
      <select id="tv-sel-${t.delivery_id}">${opciones || "<option value=''>No hay choferes reportando</option>"}</select>
      <button type="button" data-asignar="${t.delivery_id}">${candidatos[0] ? `Asignar a ${esc(nombre(candidatos[0].d))}` : "Asignar a este chofer"}</button>
      <button type="button" class="sec" data-elegir="${t.delivery_id}">Elegir tocando el mapa</button>
      ${despachoVivoDe(t) && t.despacho!.status !== "OFFERING" ? `<button type="button" class="sec" data-reofertar="${t.delivery_id}">Reofertar</button>` : ""}
      ${!despachoVivoDe(t) ? `<button type="button" class="sec" data-ofrecer="${t.delivery_id}">Ofrecer a los choferes</button>` : ""}`;
    return `<div class="t"><b>${esc(t.nombre_destinatario ?? t.tracking ?? t.delivery_id)}</b> <span style="color:${TURBO_ESTADO_COLOR[t.estado]}">${esc(st)}</span><span class="m">${vence}</span>${quien}${acciones}</div>`;
  }).join("");
  return `<div><b>${esc(s.name)}</b><div class="m">${esc(s.address ?? "")}${s.address ? " · " : ""}${esc(STORE_ESTADO[s.estado].label)}${entregados ? ` · ${entregados} entregado${entregados === 1 ? "" : "s"} hoy` : ""}${cancelados ? ` · ${cancelados} cancelado${cancelados === 1 ? "" : "s"}` : ""}</div>${filas || `<div class="t m">Ningún Turbo abierto ahora.</div>`}</div>`;
}

// Por qué un Turbo no tiene chofer (HTML). Con despacho, lo dice el despacho.
// Sin despacho, el servidor lo deduce con las reglas y la configuración de
// ahora, no las del alta: es un motivo probable y así se rotula (el servidor
// nuevo ya lo escribe en el texto; si no, lo dice el rótulo).
export function motivoSinChofer(t: Pick<TurboHoy, "despacho" | "detalle">): string {
  const d = t.detalle ?? "sin chofer";
  if (t.despacho) return `<b>Motivo:</b> ${esc(d)}`;
  return /probable/i.test(d) ? esc(d) : `<b>Motivo probable:</b> ${esc(d)}`;
}

// El pedido de la asignación a mano. Solo se ofrece asignar un Turbo que se ve
// SIN_CHOFER (popup y lista), así que lo que vio el coordinador es siempre
// «sin chofer»: va null y, si en el medio lo aceptó alguien, el servidor
// contesta 409 en vez de pisarlo. Antes salía del último sondeo, que ya podía
// traer al chofer que acababa de aceptar mientras el popup (con el foco
// adentro no se rearma) lo seguía mostrando sin chofer: el servidor lo daba
// por visto y lo reemplazaba.
export const pedidoAsignar = (turbo: Pick<TurboHoy, "delivery_id">, driverId: number) =>
  ({ delivery_id: turbo.delivery_id, driver_id: driverId, expected_driver_id: null as number | null });

export type AccionesPopup = {
  asignar: (turbo: TurboHoy, driverId: number) => void;
  elegir: (turbo: TurboHoy) => void;
  ofrecer: (turbo: TurboHoy) => void;
  reofertar: (turbo: TurboHoy) => void;
};

// Los botones del popup son HTML suelto: se escuchan por delegación en el
// contenedor del mapa. El Turbo sale del último sondeo (`live`); mientras
// corre una acción (`ocupado`) no se toma otra. Devuelve cómo dejar de escuchar.
export function escucharPopup(el: HTMLElement, live: () => VivoPopup | null, ocupado: () => boolean, acciones: AccionesPopup): () => void {
  const onClick = (ev: Event) => {
    const datos = live(); if (!datos) return;
    const b = (ev.target as HTMLElement).closest("[data-asignar],[data-elegir],[data-reofertar],[data-ofrecer]") as HTMLElement | null;
    if (!b) return;
    ev.preventDefault();
    if (ocupado()) return;
    const id = Number(b.dataset.asignar ?? b.dataset.elegir ?? b.dataset.reofertar ?? b.dataset.ofrecer);
    const turbo = datos.turbos.find((t) => t.delivery_id === id); if (!turbo) return;
    if (b.dataset.asignar) {
      const sel = el.querySelector(`#tv-sel-${id}`) as HTMLSelectElement | null;
      const driverId = Number(sel?.value); if (!driverId) return;
      acciones.asignar(turbo, driverId);
    } else if (b.dataset.elegir) acciones.elegir(turbo);
    else if (b.dataset.ofrecer) acciones.ofrecer(turbo);
    else acciones.reofertar(turbo);
  };
  // Al elegir otro chofer en el selector, el botón lo nombra.
  const onChange = (ev: Event) => {
    const sel = ev.target;
    if (sel instanceof HTMLSelectElement && sel.id.startsWith("tv-sel-")) rotularAsignar(el, sel);
  };
  el.addEventListener("click", onClick);
  el.addEventListener("change", onChange);
  return () => { el.removeEventListener("click", onClick); el.removeEventListener("change", onChange); };
}

export type PopupLike = {
  getElement(): HTMLElement | undefined;
  update(): void;
  options: { autoPan?: boolean };
};

// El botón «Asignar» dice a quién asigna: el chofer elegido en el selector.
export function rotularAsignar(root: ParentNode, sel: HTMLSelectElement) {
  const id = sel.id.replace(/^tv-sel-/, "");
  const boton = root.querySelector(`[data-asignar="${id}"]`);
  const nombre = sel.selectedOptions[0]?.dataset.nombre;
  if (boton) boton.textContent = nombre ? `Asignar a ${nombre}` : "Asignar a este chofer";
}

// Mientras el coordinador lo está usando (foco en el selector o en un botón)
// no se toca. Si no, se rearma conservando el chofer elegido de cada Turbo
// (aunque cambie el orden de la lista) y sin recentrar el mapa: el autoPan es
// para cuando se abre, no para cada sondeo. Devuelve si lo rearmó.
export function refrescarPopup(popup: PopupLike | null | undefined, activo: Element | null = document.activeElement): boolean {
  const el = popup?.getElement();
  if (!popup || !el) return false;
  if (activo && el.contains(activo)) return false;
  const elegidos = new Map<string, string>();
  el.querySelectorAll("select").forEach((s) => elegidos.set(s.id, s.value));
  const autoPan = popup.options.autoPan;
  popup.options.autoPan = false;
  try { popup.update(); } finally { popup.options.autoPan = autoPan; }
  el.querySelectorAll("select").forEach((s) => {
    const v = elegidos.get(s.id);
    if (v && Array.from(s.options).some((o) => o.value === v)) s.value = v;
    rotularAsignar(el, s);
  });
  return true;
}
